import { HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { AdvancedTableApiDeletionService } from '@mmx/components'
import { BehaviorSubject, catchError, concatMap, exhaustMap, filter, finalize, map, of, ReplaySubject, share, Subject, withLatestFrom } from 'rxjs'

import { AuthorizationRule, AuthorizationRuleData } from '../model/authorization'
import { truthyValues } from '../utils'
import { AuthenticatedRestService } from './auth-rest.service'
import { isResponse, toData } from './rest'

@Injectable()
export class AuthorizationRulesService
  extends AuthenticatedRestService
  implements AdvancedTableApiDeletionService<AuthorizationRule>
{
  public route = '/authorizations'
  public rulesRoute = `${this.route}/rules`
  public bust$ = new BehaviorSubject(false)
  public collectionParams = new ReplaySubject<Record<string, any>>(1)
  public updateParams = new Subject<Record<string, any>>()
  public deleteParams = new Subject<string>()

  private headers$ = this.bust$.pipe(
    map((bust) => (bust ? new HttpHeaders({ 'x-refresh': 'true' }) : null)),
  )

  public collectionResponse$ = this.collectionParams.pipe(
    map(truthyValues),
    map((object) => new HttpParams({ fromObject: object })),
    withLatestFrom(this.headers$),
    concatMap(([params, headers]) =>
      this.get<AuthorizationRuleData[]>(`${this.route}/rules`, params, { headers }).pipe(
        map((response) => ({
          ...response,
          data: response.data.map((rule) => new AuthorizationRule(rule)),
        })),
        catchError((error: HttpErrorResponse) => of(error)),
        finalize(() => this.bust$.next(false)),
      ),
    ),
    share(),
  )

  public collectionSuccess$ = this.collectionResponse$.pipe(
    filter(isResponse),
    map(toData),
  )

  public updateResponse$ = this.updateParams.pipe(
    map(truthyValues),
    exhaustMap((params) => this.putOrPost(params)),
    share(),
  )

  public deleteResponse$ = this.deleteParams.pipe(
    filter((id) => !!id),
    exhaustMap((id) =>
      this.delete<AuthorizationRule>(`${this.rulesRoute}/${id}`).pipe(
        catchError((error: HttpErrorResponse) => of(error)),
        finalize(() => this.bust$.next(true)),
      ),
    ),
    share(),
  )

  public getInsurancePlanTypes() {
    return this.get<Record<string, string>>(`${this.route}/insurance-plan-types`).pipe(
      catchError((error: HttpErrorResponse) => of(error)),
      map((r) => {
        if (r instanceof HttpErrorResponse) {
          return []
        }

        return Object.keys(r.data).map((code) => ({ code, name: r.data[code] }))
      }),
    )
  }

  private putOrPost = (params: Record<string, any>) => {
    if (params.id) {
      return this.put<AuthorizationRule>(`${this.rulesRoute}/${params.id}`, params)
    } else {
      return this.post<AuthorizationRule>(`${this.rulesRoute}`, params)
    }
  }
}
